import React, { useState, useEffect, useMemo } from 'react';
import { X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useMutation } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import toast from 'react-hot-toast';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import { useQuery } from '@tanstack/react-query';
export default function FullsetForm({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    nama: '',
    harga_jual: '',
    stok: '',
    catatan: '',
    status: 'aktif',
    dari_client: false,
    client_owner: '',
    ukuran_label: '',
    ukuran_aktual: '',
    vendor: ''
  });

  const { data: allBotol = [] } = useQuery({ queryKey: ['botol-all'], queryFn: () => base44.entities.Botol.list() });
  const { data: allTutup = [] } = useQuery({ queryKey: ['tutup-all'], queryFn: () => base44.entities.Tutup.list() });
  const { data: allSpray = [] } = useQuery({ queryKey: ['spray-all'], queryFn: () => base44.entities.Spray.list() });
  const { data: allFullset = [] } = useQuery({ queryKey: ['fullset-all'], queryFn: () => base44.entities.Fullset.list() });

  const vendorOptions = useMemo(() => {
    const map = new Set();
    [...allBotol, ...allTutup, ...allSpray, ...allFullset].forEach(b => {
      if (b.vendor && !b.dari_client) map.add(b.vendor.trim());
    });
    return Array.from(map).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [allBotol, allTutup, allSpray, allFullset]);

  useEffect(() => {
    if (item) {
      setForm({
        nama: item.nama || '',
        harga_rupiah: item.harga_rupiah || '',
        harga_jual: item.harga_jual || '',
        stok: item.stok !== undefined ? item.stok : '',
        catatan: item.catatan || '',
        status: item.status || 'aktif',
        dari_client: item.dari_client || false,
        client_owner: item.client_owner || '',
        ukuran_label: item.ukuran_label || item.ukuran || '',
        ukuran_aktual: item.ukuran_aktual || '',
        vendor: item.vendor || item.supplier || ''
      });
    }
  }, [item]);

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      if (item?.id) {
        const res = await base44.entities.Fullset.update(item.id, payload);
        if (item.nama && payload.nama && item.nama.trim() !== payload.nama.trim()) {
          try {
            const dbList = await base44.entities.DatabaseBahan.list();
            for (const db of dbList) {
              if (db.fullset_nama === item.nama.trim()) {
                await base44.entities.DatabaseBahan.update(db.id, { fullset_nama: payload.nama.trim() });
              }
            }
          } catch (err) {
            console.error('Failed to cascade update Fullset name in DatabaseBahan', err);
          }
        }
        return res;
      }
      return await base44.entities.Fullset.create(payload);
    },
    onSuccess: () => {
      toast.success(item ? 'Berhasil mengupdate Fullset' : 'Fullset berhasil ditambahkan');
      onSaved();
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nama) {
      toast.error('Nama wajib diisi');
      return;
    }
    const payload = {
      ...form,
      harga_rupiah: parseFloat(form.harga_rupiah) || 0,
      harga_jual: parseFloat(form.harga_jual) || 0,
      stok: parseInt(form.stok, 10) || 0,
      dari_client: form.dari_client,
      client_owner: form.dari_client ? form.client_owner : '',
      ukuran_label: form.ukuran_label,
      ukuran_aktual: form.ukuran_aktual,
      ukuran: form.ukuran_label,
      vendor: form.vendor
    };
    saveMut.mutate(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40  flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-emerald-50/50">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              {item ? 'Edit Fullset Packaging' : 'Tambah Fullset Baru'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Gabungan Botol, Tutup, dan Spray dengan harga paket</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-700 mb-2 block">Kepemilikan Barang</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm(p => ({ ...p, dari_client: false }))} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${!form.dari_client ? 'bg-accent text-accent-foreground border-accent shadow-sm' : 'border-border text-muted-foreground hover:border-accent/50'}`}>Inventori Satala</button>
              <button type="button" onClick={() => { setForm(p => ({ ...p, dari_client: true, harga_rupiah: 0, harga_jual: 0 })); }} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.dari_client ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'border-border text-muted-foreground hover:border-emerald-500/50'}`}>Titipan Client</button>
            </div>
          </div>

          {form.dari_client && (
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 mt-2">
              <label className="text-xs font-bold text-emerald-800 mb-1 block">Nama Client / Brand Titipan *</label>
              <Input 
                value={form.client_owner} 
                onChange={e => setForm(p => ({...p, client_owner: e.target.value}))} 
                placeholder="Misal: Dermatech, atau Klinik Bunga..." 
                className="border-emerald-200 focus-visible:ring-emerald-500 bg-white"
              />
              <p className="text-[10px] text-emerald-600 mt-1">Isi dengan nama brand klien secara persis agar tercatat di Data Klien mereka.</p>
            </div>
          )}

          {!form.dari_client && (
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Vendor / Supplier</label>
              <SearchableCombobox
                value={form.vendor}
                onValueChange={v => setForm(p => ({...p, vendor: v}))}
                options={vendorOptions}
                placeholder="Pilih atau ketik vendor baru..."
                searchPlaceholder="Cari vendor..."
                allowCustom={true}
              />
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">Nama Paket Fullset *</label>
            <Input 
              value={form.nama} 
              onChange={e => setForm(p => ({...p, nama: e.target.value}))} 
              placeholder="Contoh: Set Botol Kaca 30ml + Pump Gold" 
              className="font-medium"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Ukuran Label (ml)</label>
              <Input 
                value={form.ukuran_label} 
                onChange={e => setForm(p => ({...p, ukuran_label: e.target.value}))} 
                placeholder="Misal: 30" 
              />
              <p className="text-[9px] text-slate-400 mt-1">Ukuran yang tertera pada kemasan</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Ukuran Aktual (ml)</label>
              <Input 
                value={form.ukuran_aktual} 
                onChange={e => setForm(p => ({...p, ukuran_aktual: e.target.value}))} 
                placeholder="Misal: 33" 
              />
              <p className="text-[9px] text-slate-400 mt-1">Kapasitas maksimal sesungguhnya</p>
            </div>
          </div>

          {!form.dari_client && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1 block">Harga Beli Paket (Rp) *</label>
                <Input 
                  type="number" 
                  value={form.harga_rupiah} 
                  onChange={e => setForm(p => ({...p, harga_rupiah: e.target.value}))} 
                  placeholder="0" 
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1 block">Harga Jual Paket (Rp)</label>
                <Input 
                  type="number" 
                  value={form.harga_jual} 
                  onChange={e => setForm(p => ({...p, harga_jual: e.target.value}))} 
                  placeholder="0" 
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Stok Tersedia (pcs) *</label>
              <Input 
                type="number" 
                value={form.stok} 
                onChange={e => setForm(p => ({...p, stok: e.target.value}))} 
                placeholder="0" 
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Status</label>
              <select 
                value={form.status} 
                onChange={e => setForm(p => ({...p, status: e.target.value}))}
                className="w-full text-sm border-slate-200 rounded-md h-9 px-3"
              >
                <option value="aktif">Aktif</option>
                <option value="pasif">Pasif</option>
                <option value="nonaktif">Tidak Aktif</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 mb-1 block">Catatan Tambahan</label>
            <Textarea 
              value={form.catatan} 
              onChange={e => setForm(p => ({...p, catatan: e.target.value}))} 
              placeholder="Contoh: Sudah termasuk segel plastik..."
              className="resize-none h-20 text-sm"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-slate-50 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>Batal</Button>
          <Button onClick={handleSubmit} disabled={saveMut.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6">
            {saveMut.isPending ? 'Menyimpan...' : 'Simpan Fullset'}
          </Button>
        </div>

      </div>
    </div>
  );
}
